'use client';

import { PrescriptionCancelButton } from '@/features/prescription-cancellation';

import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { toast } from 'sonner';
import {
  ArrowRight,
  Check,
  Download,
  FileText,
  File as FileIcon,
  Image as ImageIcon,
  Loader2,
  ListChecks,
  MoreHorizontal,
  Paperclip,
  Pencil,
  Sparkles,
} from 'lucide-react';

import type { JsonValue } from '@lotushealth/lotushealth_js_sdk/schemas';

import {
  downloadPriorAuthAttachment,
  usePriorAuthActionMutation,
  usePriorAuthDetailsQuery,
  type PriorAuthActionBody,
  type PriorAuthAnsweredQuestionDetailT,
  type PriorAuthorizationAnswerDetailT,
  type PriorAuthorizationAnswerRequestT,
  type PriorAuthorizationQuestionT,
} from '@/entities/prior-auth';
import { usePharmacyByIdQuery, usePrescriptionQuery } from '@/entities/rx';
import { useProposedActionMutations } from '@/features/proposed-actions-plan/model/useProposedActionMutations';
import { LabeledFieldGrid } from '@/shared/ui/primitives';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Button } from '@/shared/ui/shadcn/button';
import { Checkbox } from '@/shared/ui/shadcn/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/shadcn/dropdown-menu';
import { HoldToSubmit } from '@/shared/ui/HoldToSubmit';
import { Input } from '@/shared/ui/shadcn/input';
import { Label } from '@/shared/ui/shadcn/label';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { useCopilotOverlays } from '@/shared/stores/ui';
import { logger, type SourceProposedActionT } from '@/shared/lib';
import { countClinicianUploads } from '@/features/prior-auth-form/model/priorAuthAttachmentRules';
import {
  describePriorAuthWorkflow,
  formatMedicationName,
} from '@/features/prior-auth-tab/model/priorAuthPresentation';

import { type PriorAuthActionPrefill } from '../model/priorAuthActionPrefill';
import {
  buildPriorAuthAnswerPayload,
  getPriorAuthAdditionalTextRequirement,
  presentPriorAuthActions,
} from '../model/priorAuthActions';
import { extractLmnErrorDetail, streamLmnDraft, type LmnCaseDraftT } from '../api/lmnApi';

import { LmnDraftReview } from './LmnDraftReview';

/** Strip the `data:<mime>;base64,` prefix that ``FileReader.readAsDataURL`` emits. */
function stripDataUrlPrefix(dataUrl: string): string {
  const commaIdx = dataUrl.indexOf(',');
  return commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl;
}

async function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Unexpected file reader result'));
        return;
      }
      resolve(stripDataUrlPrefix(result));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Attachment filename → Lucide icon. Just enough fidelity to visually
 * distinguish PDFs from images from "anything else" — DoseSpot doesn't
 * give us a content-type so we go off the extension.
 */
function attachmentIcon(fileName: string | null | undefined) {
  const name = (fileName ?? '').toLowerCase();
  if (name.endsWith('.pdf')) {
    return FileText;
  }
  if (/\.(png|jpe?g|gif|webp|bmp|tiff?|svg|heic)$/i.test(name)) {
    return ImageIcon;
  }
  return FileIcon;
}

/** Pretty short upload date for the attachment row, e.g. "May 1, 2026". */
function formatAttachmentDate(stamp: string | null | undefined): string | null {
  if (!stamp) {
    return null;
  }
  const parsed = new Date(stamp);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Hand a downloaded blob to the browser as a "Save As" prompt. Pulled
 * out so the entity-layer ``downloadPriorAuthAttachment`` (which only
 * knows about transport) stays free of DOM concerns.
 */
function triggerBrowserDownload(blob: Blob, suggestedFileName: string): void {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = suggestedFileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // Defer revocation a tick so the browser can start the save.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }
}

function toJsonValueRecord(value: Record<string, unknown>): Record<string, JsonValue> {
  const result: Record<string, JsonValue> = {};
  Object.entries(value).forEach(([key, entryValue]) => {
    if (entryValue !== null && entryValue !== undefined) {
      result[key] = entryValue as JsonValue;
    }
  });
  return result;
}

/** Render a single answer detail row as a plain string for the transcript.
 *
 * A choice answer (``Answer``) and its accompanying additional free text
 * (``AnswerFreeText``) can both be populated on the same row — this happens
 * on Select/Multiselect choices that allow an "additional text" note. In that
 * case DoseSpot returns the chosen choice text in ``Answer`` and the note in
 * ``AnswerFreeText``, so we render both (e.g. ``Other — see chart notes``)
 * rather than dropping the note. */
function formatAnswerDetail(detail: PriorAuthorizationAnswerDetailT): string {
  const choice =
    detail.Answer !== null && detail.Answer !== undefined && detail.Answer !== ''
      ? detail.Answer
      : null;
  const freeText =
    detail.AnswerFreeText !== null &&
    detail.AnswerFreeText !== undefined &&
    detail.AnswerFreeText !== ''
      ? detail.AnswerFreeText
      : null;
  if (choice !== null && freeText !== null) {
    return `${choice} — ${freeText}`;
  }
  if (choice !== null) {
    return choice;
  }
  if (freeText !== null) {
    return freeText;
  }
  if (detail.AnswerNumeric !== null && detail.AnswerNumeric !== undefined) {
    return String(detail.AnswerNumeric);
  }
  if (detail.AnswerDateTime !== null && detail.AnswerDateTime !== undefined) {
    return String(detail.AnswerDateTime);
  }
  return '—';
}

/** Collapse an answered question's answer details into one display string. */
function formatAnswers(answers: PriorAuthorizationAnswerDetailT[] | null | undefined): string[] {
  return (answers ?? []).map(formatAnswerDetail);
}

/**
 * Wizard for acting on a Prior Authorization case.
 *
 * Conversation-style UI:
 * - Previously answered questions are shown as a greyed-out transcript at
 *   the top (payer's question + clinician's answer).
 * - Any currently pending question is rendered below as an editable form.
 * - The primary footer button saves the current answer or sends the completed
 *   form. Supporting-document controls appear only when insurance requires
 *   them.
 * - Additional actions are tucked into an overflow menu and shown only when
 *   the backend includes them in ``allowedActions``.
 *
 * Driven by the ``prefill`` prop (delivered by the overlay). The prefill
 * always carries a ``recordId`` (our internal UUID) so we can fetch live case
 * details and record actions against the same durable case.
 */
interface PriorAuthActionWizardProps {
  prefill: PriorAuthActionPrefill;
  patientUserId: string;
}

export function PriorAuthActionWizard({ prefill, patientUserId }: PriorAuthActionWizardProps) {
  const mutations = useProposedActionMutations();
  const priorAuthActionMutation = usePriorAuthActionMutation();
  const overlays = useCopilotOverlays();

  const activePrefill = prefill;
  const sourceProposedActionRef = useRef<SourceProposedActionT | null>(
    prefill.sourceProposedAction ??
      (prefill.action ? { externalId: prefill.action.external_id } : null)
  );

  const [submitting, setSubmitting] = useState(false);

  const [selectedChoices, setSelectedChoices] = useState<number[]>([]);
  const [freeTextAnswer, setFreeTextAnswer] = useState('');
  const [numericAnswer, setNumericAnswer] = useState('');
  const [dateAnswer, setDateAnswer] = useState('');

  const [appealOpen, setAppealOpen] = useState(false);
  const [appealReason, setAppealReason] = useState('');
  const [decision, setDecision] = useState<'SELF_PAY' | 'END_PLAN' | null>(null);
  const [decisionReason, setDecisionReason] = useState('');
  const [patientAgreed, setPatientAgreed] = useState(false);

  const [lmnDraft, setLmnDraft] = useState<LmnCaseDraftT | null>(null);
  const [lmnMarkdown, setLmnMarkdown] = useState('');
  const [lmnDrafting, setLmnDrafting] = useState(false);
  const [lmnProgress, setLmnProgress] = useState(
    'Preparing the prior authorization for chart review.'
  );
  const [lmnError, setLmnError] = useState<string | null>(null);
  const [attachmentStep, setAttachmentStep] = useState<'answers' | 'letter'>('answers');
  const [letterReviewOpen, setLetterReviewOpen] = useState(false);

  // Which previously-answered question the clinician is currently editing,
  // if any. ``null`` means the form is bound to the payer's pending question
  // (the default). When set, the answer form re-renders that past question
  // (pre-filled with the prior answer) and the primary action POSTs a fresh
  // answer to *this* question id — DoseSpot accepts re-answers to an
  // already-answered question until the case is submitted.
  //
  // Question ids are strings (DoseSpot ids can exceed JS's safe-integer
  // range, so we never let them round-trip through a JS ``number``).
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);

  const [formError, setFormError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const detailsQuery = usePriorAuthDetailsQuery(activePrefill?.recordId, {
    refetchInterval: 30_000,
  });

  const recordId = activePrefill?.recordId ?? '';
  const dosespotCase = detailsQuery.data?.dosespotCase;
  const caseId = Number(dosespotCase?.caseNumber ?? 0);
  useEffect(() => {
    setLmnDraft(null);
    setLmnMarkdown('');
    setLmnProgress('Preparing the prior authorization for chart review.');
    setLmnError(null);
    setAttachmentStep('answers');
    setLetterReviewOpen(false);
  }, [recordId]);

  const dosespotPrescriptionId = useMemo(() => {
    if (activePrefill?.dosespotPrescriptionId) {
      return activePrefill.dosespotPrescriptionId;
    }
    const fromCase = dosespotCase?.case.PrescriptionId;
    return fromCase != null ? String(fromCase) : '';
  }, [activePrefill?.dosespotPrescriptionId, dosespotCase]);

  const prescriptionQuery = usePrescriptionQuery(patientUserId, dosespotPrescriptionId);

  const pharmacyId = prescriptionQuery.data?.PharmacyId
    ? String(prescriptionQuery.data.PharmacyId)
    : undefined;
  const pharmacyQuery = usePharmacyByIdQuery(pharmacyId);
  const pharmacyDisplayName = pharmacyQuery.data?.StoreName
    ? String(pharmacyQuery.data.StoreName)
    : undefined;

  const diagnosis = useMemo(() => {
    const primary = prescriptionQuery.data?.FirstPrescriptionDiagnosis?.PrimaryDiagnosis;
    if (!primary?.DiagnosisCode) {
      return null;
    }
    return [primary.DiagnosisCode, primary.DiagnosisDescription].filter(Boolean).join(' · ');
  }, [prescriptionQuery.data]);

  const pendingQuestion: PriorAuthorizationQuestionT | null = useMemo(() => {
    return dosespotCase?.pendingQuestion ?? null;
  }, [dosespotCase]);

  // Use the server-minted string id, not ``case.NextQuestionId``: the latter
  // is a JS ``number`` and would silently truncate a large DoseSpot id. The
  // backend stringifies the full-precision id into ``pendingQuestionId``.
  const pendingQuestionId = useMemo(() => {
    return dosespotCase?.pendingQuestionId ?? null;
  }, [dosespotCase]);

  // Previously-answered questions resolved back to their DoseSpot ids by the
  // backend. These are re-answerable (clickable) while the case is still
  // clinician-owned; the backend omits them once the case is closed.
  const answeredQuestions = useMemo<PriorAuthAnsweredQuestionDetailT[]>(() => {
    return dosespotCase?.answeredQuestions ?? [];
  }, [dosespotCase]);

  // Read-only Q/A transcript flattened from the DoseSpot history. Used for
  // closed cases (where ``answeredQuestions`` is intentionally empty) and as
  // a fallback so a case with history but no recorded ids still shows what
  // was answered.
  const closedTranscript = useMemo(() => {
    const sets = dosespotCase?.history?.QuestionSets ?? [];
    const flat: { question: string; answers: string[] }[] = [];
    for (const set of sets) {
      for (const aq of set?.AnsweredQuestions ?? []) {
        flat.push({ question: aq.Question ?? '—', answers: formatAnswers(aq.Answers) });
      }
    }
    return flat;
  }, [dosespotCase]);

  // The question the editable answer form is currently bound to: the
  // clinician's chosen "edit a previous answer" target when set, otherwise
  // the payer's pending question.
  const editingTarget = useMemo<PriorAuthAnsweredQuestionDetailT | null>(() => {
    if (editingQuestionId === null) {
      return null;
    }
    return answeredQuestions.find((aq) => aq.questionId === editingQuestionId) ?? null;
  }, [answeredQuestions, editingQuestionId]);

  const isEditingPrevious = editingTarget !== null;
  const earlierAnswers = useMemo(
    () => answeredQuestions.filter((answer) => answer.questionId !== editingQuestionId),
    [answeredQuestions, editingQuestionId]
  );

  // The question whose form is rendered + submitted right now.
  const activeQuestion: PriorAuthorizationQuestionT | null = isEditingPrevious
    ? (editingTarget?.question ?? null)
    : pendingQuestion;
  const activeQuestionId: string | null = isEditingPrevious
    ? (editingTarget?.questionId ?? null)
    : pendingQuestionId;

  const answerType = activeQuestion?.AnswerType ?? null;
  const additionalTextRequirement = activeQuestion
    ? getPriorAuthAdditionalTextRequirement(activeQuestion, selectedChoices)
    : 'hidden';
  const currentStatus = dosespotCase?.case.CurrentStatus ?? null;
  const presentedActions = presentPriorAuthActions(
    detailsQuery.data?.allowedActions ?? [],
    detailsQuery.data?.isCurrentClinicianPrescriber !== false
  );
  const isTerminal = detailsQuery.data?.currentOwner === 'NONE';
  const isWaiting =
    detailsQuery.data?.currentOwner === 'PAYER' ||
    detailsQuery.data?.currentOwner === 'LOTUS_AUTOMATION';
  // ``CanAppeal`` lives on ``case.FinalStatus`` as a bool, but the history
  // endpoint echoes it back as a "Yes"/"No" string. Support both.
  const canAppeal = presentedActions.appeal;
  const isExpedited = dosespotCase?.case.IsExpedited === true;
  const workflowCopy = detailsQuery.data
    ? describePriorAuthWorkflow(detailsQuery.data.workflowState)
    : null;
  const medicationLabel = formatMedicationName(
    prescriptionQuery.data?.DisplayName,
    prescriptionQuery.data?.Strength
  );
  const medicationHeading =
    medicationLabel === 'Medication unavailable' ? 'Prior authorization' : medicationLabel;
  const prescriptionSummary = [
    prescriptionQuery.data?.Directions,
    prescriptionQuery.data?.DaysSupply ? `${prescriptionQuery.data.DaysSupply} days` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  // True when the wizard has an open question form, either the payer's
  // pending question or a previous answer the clinician chose to edit.
  const hasPendingQuestion = activeQuestion !== null && activeQuestionId !== null;

  // DoseSpot sets this when insurance requires supporting documentation.
  // Do not ask for documents otherwise because unnecessary attachments can
  // divert an electronic request into manual review.
  const caseRequiresAttachment = !!dosespotCase?.case.AttachmentRequired;
  const uploadedAttachmentCount = countClinicianUploads(dosespotCase?.case.Attachments);
  const submitBlockedByMissingAttachment = caseRequiresAttachment && uploadedAttachmentCount === 0;
  const isAttachmentWorkflow =
    caseRequiresAttachment &&
    uploadedAttachmentCount === 0 &&
    !hasPendingQuestion &&
    !isTerminal &&
    !isWaiting;
  const isReviewingCompletedAnswers = isAttachmentWorkflow && attachmentStep === 'answers';
  const showAnswerHistory = !isAttachmentWorkflow || isReviewingCompletedAnswers;

  /** Whether the answer form is filled out well enough to submit. */
  const answerIsComplete = useMemo(() => {
    if (!activeQuestion) {
      return false;
    }
    return (
      buildPriorAuthAnswerPayload(activeQuestion, {
        selectedChoices,
        freeTextAnswer,
        numericAnswer,
        dateAnswer,
      }) !== null
    );
  }, [activeQuestion, dateAnswer, freeTextAnswer, numericAnswer, selectedChoices]);

  // Save one answer or send the completed question set.
  const primaryMode = isEditingPrevious
    ? 'answer_question'
    : (presentedActions.primary ?? 'submit');
  const hasPrimaryAction = isEditingPrevious || presentedActions.primary !== null;
  const primaryNeedsPrescriber = !isEditingPrevious && presentedActions.primaryNeedsPrescriber;
  const primaryLabel = primaryNeedsPrescriber
    ? 'Prescriber required'
    : submitting
      ? 'Submitting…'
      : primaryMode === 'answer_question'
        ? isEditingPrevious
          ? 'Save changes'
          : 'Save answer'
        : primaryMode === 'submit'
          ? 'Send to insurance'
          : 'Send to pharmacy';
  const primaryDisabled =
    submitting ||
    !hasPrimaryAction ||
    primaryNeedsPrescriber ||
    (primaryMode === 'submit' && submitBlockedByMissingAttachment) ||
    (primaryMode === 'answer_question' && !answerIsComplete);

  const handleCreateSupportingLetter = useCallback(async () => {
    if (!recordId) {
      setLmnError('Missing prior authorization record.');
      return;
    }

    setLmnDrafting(true);
    setLmnProgress('Preparing the prior authorization for chart review.');
    setLmnError(null);
    try {
      let completed = false;
      for await (const event of streamLmnDraft({
        patient_user_id: patientUserId,
        prior_auth_record_id: recordId,
      })) {
        if (event.type === 'progress') {
          setLmnProgress(event.message);
          continue;
        }

        completed = true;
        setLmnDraft(event.draft);
        setLmnMarkdown(event.draft.markdown);
        setAttachmentStep('letter');
        setLetterReviewOpen(true);
      }
      if (!completed) {
        throw new Error('The supporting letter draft ended before it was complete.');
      }
    } catch (error) {
      setLmnError(extractLmnErrorDetail(error));
    } finally {
      setLmnDrafting(false);
    }
  }, [patientUserId, recordId]);

  const handleFilePick = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }
      if (!recordId) {
        setFormError('Missing prior authorization record.');
        return;
      }
      try {
        const b64 = await readFileAsBase64(file);
        setSubmitting(true);
        setFormError(null);
        await priorAuthActionMutation.mutateAsync({
          recordId,
          request: {
            action: 'upload_attachment',
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
            contentBase64: b64,
          },
          patientUserId,
        });
        toast.success('Attachment uploaded.', { description: file.name });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setFormError(`Attachment upload failed: ${message}`);
        logger.error('priorAuth.uploadAttachment.failed', { err });
      } finally {
        setSubmitting(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [patientUserId, priorAuthActionMutation, recordId]
  );

  const buildAnswerPayload = useCallback((): PriorAuthorizationAnswerRequestT | null => {
    if (!activeQuestion) {
      return null;
    }
    return buildPriorAuthAnswerPayload(activeQuestion, {
      selectedChoices,
      freeTextAnswer,
      numericAnswer,
      dateAnswer,
    });
  }, [activeQuestion, dateAnswer, freeTextAnswer, numericAnswer, selectedChoices]);

  const resetAnswerInputs = useCallback(() => {
    setSelectedChoices([]);
    setFreeTextAnswer('');
    setNumericAnswer('');
    setDateAnswer('');
  }, []);

  /** Enter "edit a previous answer" mode for a specific answered question,
   *  pre-filling the form with the clinician's prior response so a small
   *  tweak doesn't require re-entering everything. Select/Multiselect
   *  choices are matched back by the choice text DoseSpot echoes in the
   *  history (there's no choice id in the answer detail). */
  const beginEditingQuestion = useCallback(
    (target: PriorAuthAnsweredQuestionDetailT) => {
      setFormError(null);
      resetAnswerInputs();
      setEditingQuestionId(target.questionId);

      const answers = target.answers ?? [];
      const answerType = target.question?.AnswerType ?? null;
      switch (answerType) {
        case 'Select':
        case 'Multiselect': {
          const answerTexts = new Set(
            answers.map((a) => (a.Answer ?? '').trim()).filter((t) => t.length > 0)
          );
          const choiceIds = (target.question?.AnswerChoices ?? [])
            .filter(
              (c) =>
                typeof c.QuestionChoiceId === 'number' &&
                c.QuestionChoiceText != null &&
                answerTexts.has(c.QuestionChoiceText.trim())
            )
            .map((c) => c.QuestionChoiceId as number);
          setSelectedChoices(choiceIds);
          // Carry any free-text portion (payers allow an "other" note on
          // some choice questions) into the notes field.
          const freeText = answers.find(
            (a) => a.AnswerFreeText != null && a.AnswerFreeText !== ''
          )?.AnswerFreeText;
          if (freeText) {
            setFreeTextAnswer(freeText);
          }
          break;
        }
        case 'FreeText': {
          const freeText =
            answers.find((a) => a.AnswerFreeText != null && a.AnswerFreeText !== '')
              ?.AnswerFreeText ??
            answers.find((a) => a.Answer != null && a.Answer !== '')?.Answer ??
            '';
          setFreeTextAnswer(freeText);
          break;
        }
        case 'Numeric': {
          const numeric = answers.find((a) => a.AnswerNumeric != null)?.AnswerNumeric;
          if (numeric != null) {
            setNumericAnswer(String(numeric));
          }
          break;
        }
        case 'Date':
        case 'DateTime': {
          const dt = answers.find((a) => a.AnswerDateTime != null)?.AnswerDateTime;
          // DoseSpot returns ISO datetimes; the ``date`` input needs
          // ``YYYY-MM-DD`` and ``datetime-local`` needs ``YYYY-MM-DDTHH:mm``.
          if (dt) {
            const iso = String(dt);
            setDateAnswer(answerType === 'Date' ? iso.slice(0, 10) : iso.slice(0, 16));
          }
          break;
        }
        default:
          break;
      }
    },
    [resetAnswerInputs]
  );

  /** Leave edit mode and clear the form back to the pending-question view. */
  const cancelEditingQuestion = useCallback(() => {
    setEditingQuestionId(null);
    resetAnswerInputs();
    setFormError(null);
  }, [resetAnswerInputs]);

  /** Run the primary action: either answer the current question or submit. */
  const handlePrimary = useCallback(async () => {
    if (!recordId) {
      setFormError('Missing prior authorization record.');
      return;
    }

    setFormError(null);
    setSubmitting(true);

    // Capture whether this submit is a re-answer of a previous question up
    // front — the mutation's refetch can clear ``editingQuestionId``'s
    // target out from under us before the success branch runs.
    const wasEditingPrevious = isEditingPrevious;

    try {
      let req: PriorAuthActionBody;
      if (primaryMode === 'answer_question') {
        if (!activeQuestionId) {
          setFormError('No question selected to answer.');
          return;
        }
        const answer = buildAnswerPayload();
        if (!answer) {
          setFormError('Please provide an answer before submitting.');
          return;
        }
        req = {
          action: 'answer_question',
          questionId: activeQuestionId,
          answer,
        };
      } else if (primaryMode === 'submit') {
        req = { action: 'submit' };
      } else {
        req = { action: 'send_prescription' };
      }

      const response = await priorAuthActionMutation.mutateAsync({
        recordId,
        request: req,
        patientUserId,
      });

      if (response.outcome === 'REJECTED') {
        throw new Error(
          response.rejectionDetail ?? response.rejectionCode ?? 'Insurance rejected this action.'
        );
      }
      if (response.outcome === 'CONFIRMATION_PENDING') {
        toast.info('Waiting for insurance to confirm receipt.');
        return;
      }

      // Answer chains: DoseSpot sometimes issues the PA question set one at
      // a time, driven by branching logic on prior answers. When the server
      // echoes back a ``nextQuestionId`` we keep the wizard open — the
      // mutation already invalidates the details query above, so the new
      // ``pendingQuestion`` refetches on the next tick and the form
      // re-renders with fresh choices. Re-answering a *previous* question
      // never advances a chain; it just revises one answer in place.
      const hasMoreQuestions =
        primaryMode === 'answer_question' &&
        !wasEditingPrevious &&
        response.currentCase.dosespotCase?.pendingQuestionId !== null &&
        response.currentCase.dosespotCase?.pendingQuestionId !== undefined;

      // Editing a previous answer isn't the "proposed action" the copilot
      // staged (that was answering the pending question / submitting), so we
      // don't finalize the source proposed-action on a re-answer.
      const source = sourceProposedActionRef.current;
      if (source && !hasMoreQuestions && !wasEditingPrevious) {
        const toolOutput = toJsonValueRecord({
          caseId,
          action: primaryMode,
          nextQuestionId: response.currentCase.dosespotCase?.pendingQuestionId ?? null,
        });
        await mutations.finalizeFromSource(source, toolOutput).catch((err: unknown) => {
          logger.error('priorAuth.finalizeFromSource.failed', {
            externalId: source.externalId,
            err,
          });
        });
      }

      if (wasEditingPrevious) {
        // Revised a prior answer: drop back to the pending-question view.
        setEditingQuestionId(null);
        resetAnswerInputs();
        setFormError(null);
      } else if (hasMoreQuestions) {
        resetAnswerInputs();
        setFormError(null);
      } else if (primaryMode === 'answer_question') {
        resetAnswerInputs();
      } else if (primaryMode === 'submit') {
        toast.success('Prior authorization sent to insurance.');
        // Don't auto-close: the clinician should see the wizard transition
        // to the locked "Submitted to payer" state so they have a clear
        // visual confirmation that the case is now with the payer.
      } else {
        toast.success('Prescription sent to the pharmacy.');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setFormError(`Action failed: ${message}`);
      logger.error('priorAuth.submit.failed', { mode: primaryMode, err });
    } finally {
      setSubmitting(false);
    }
  }, [
    activeQuestionId,
    buildAnswerPayload,
    caseId,
    isEditingPrevious,
    mutations,
    patientUserId,
    primaryMode,
    priorAuthActionMutation,
    recordId,
    resetAnswerInputs,
  ]);

  /** Run an action selected from the overflow menu. */
  const runSimpleAction = useCallback(
    async (req: PriorAuthActionBody, successToast: string) => {
      if (!recordId) {
        setFormError('Missing prior authorization record.');
        return;
      }
      setFormError(null);
      setSubmitting(true);
      try {
        const response = await priorAuthActionMutation.mutateAsync({
          recordId,
          request: req,
          patientUserId,
        });
        if (response.outcome === 'REJECTED') {
          throw new Error(
            response.rejectionDetail ?? response.rejectionCode ?? 'Insurance rejected this action.'
          );
        }
        if (response.outcome === 'CONFIRMATION_PENDING') {
          toast.info('Waiting for insurance to confirm receipt.');
          return;
        }

        const source = sourceProposedActionRef.current;
        if (source) {
          const toolOutput = toJsonValueRecord({ caseId, action: req.action });
          await mutations.finalizeFromSource(source, toolOutput).catch((err: unknown) => {
            logger.error('priorAuth.finalizeFromSource.failed', {
              externalId: source.externalId,
              err,
            });
          });
        }

        toast.success(successToast);
        overlays.close('priorAuthAction');
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setFormError(`Action failed: ${message}`);
        logger.error('priorAuth.simpleAction.failed', { action: req.action, err });
      } finally {
        setSubmitting(false);
      }
    },
    [caseId, mutations, overlays, patientUserId, priorAuthActionMutation, recordId]
  );

  const handleAppealConfirm = useCallback(async () => {
    if (!appealReason.trim()) {
      setFormError('Appeal reason is required.');
      return;
    }
    await runSimpleAction(
      {
        action: 'appeal',
        appealReason: appealReason.trim(),
      },
      'Appeal filed.'
    );
    setAppealOpen(false);
    setAppealReason('');
  }, [appealReason, runSimpleAction]);

  const handleDecision = useCallback(async () => {
    if (!decision || !recordId) {
      return;
    }
    if (!decisionReason.trim()) {
      setFormError('Please add a reason.');
      return;
    }
    if (decision === 'SELF_PAY' && !patientAgreed) {
      setFormError('Confirm that the patient agreed to self-pay.');
      return;
    }
    const request: PriorAuthActionBody =
      decision === 'SELF_PAY'
        ? { action: 'self_pay', reason: decisionReason.trim(), patientAgreed: true }
        : { action: 'end_plan', reason: decisionReason.trim() };
    await runSimpleAction(
      request,
      decision === 'SELF_PAY' ? 'Prescription sent as self-pay.' : 'Treatment ended.'
    );
  }, [decision, decisionReason, patientAgreed, recordId, runSimpleAction]);

  const toggleChoice = (choiceId: number) => {
    if (answerType === 'Select') {
      setSelectedChoices([choiceId]);
      return;
    }
    setSelectedChoices((prev) =>
      prev.includes(choiceId) ? prev.filter((id) => id !== choiceId) : [...prev, choiceId]
    );
  };

  // Which of the destructive/terminal actions are surfaced right now?
  const showAppeal = canAppeal;
  const showSelfPay = presentedActions.selfPay;
  const hasOverflowActions = showAppeal || showSelfPay;

  if (!activePrefill) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        No prior authorization selected.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-3 overflow-hidden p-3">
      <div className="px-1 pb-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold">{medicationHeading}</h2>
            {prescriptionSummary ? (
              <p className="mt-0.5 text-sm text-muted-foreground">{prescriptionSummary}</p>
            ) : null}
          </div>
          <div className="flex flex-wrap justify-end gap-1.5">
            {isExpedited ? (
              <span className="rounded-full border border-orange-400/50 bg-orange-50 px-2.5 py-1 text-[10px] font-medium uppercase tracking-wide text-orange-700 dark:border-orange-500/30 dark:bg-orange-950/40 dark:text-orange-300">
                Expedited
              </span>
            ) : null}
          </div>
        </div>
        {workflowCopy && (isWaiting || isTerminal) ? (
          <p className="mt-2 text-sm leading-relaxed text-foreground/75">
            {workflowCopy.description}
          </p>
        ) : null}
      </div>

      {detailsQuery.isLoading ? (
        <div className="text-xs text-muted-foreground">Loading prior authorization…</div>
      ) : null}

      {detailsQuery.isError ? (
        <Alert variant="destructive">
          <AlertTitle>Could not load prior authorization</AlertTitle>
          <AlertDescription>
            {detailsQuery.error instanceof Error
              ? detailsQuery.error.message
              : 'Unknown error loading this prior authorization.'}
          </AlertDescription>
        </Alert>
      ) : null}

      {formError ? (
        <Alert variant="destructive">
          <AlertDescription className="text-xs">{formError}</AlertDescription>
        </Alert>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
        {showAnswerHistory && earlierAnswers.length > 0 ? (
          <div className="space-y-2">
            <Label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Earlier answers
            </Label>
            {/* Each answered question is re-openable: DoseSpot lets you POST
                a fresh answer to its question id until the case is submitted,
                so clicking one loads it back into the form (pre-filled) for
                revision. Editing is only offered while the case is still
                clinician-owned (not waiting/terminal). */}
            <ol className="space-y-2">
              {earlierAnswers.map((aq) => {
                const answers = formatAnswers(aq.answers);
                // Editable when we have the live question detail and the case
                // is still open for clinician input.
                const canEdit = aq.question != null && !isTerminal && !isWaiting && !submitting;
                return (
                  <li
                    key={aq.questionId}
                    className="rounded-xl border border-border bg-muted/20 p-3"
                  >
                    <div className="flex items-start gap-2">
                      <span className="mt-0.5 inline-flex h-4 w-4 flex-none items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                        <Check className="h-3 w-3" aria-hidden />
                      </span>
                      <div className="min-w-0 flex-1 space-y-1">
                        <p className="text-xs font-medium leading-relaxed whitespace-pre-wrap">
                          {aq.questionText ?? aq.question?.QuestionText ?? '—'}
                        </p>
                        {answers.length > 0 ? (
                          <ul className="space-y-0.5">
                            {answers.map((ans, aIdx) => (
                              <li
                                key={aIdx}
                                className="text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap"
                              >
                                {ans}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-xs text-muted-foreground italic">
                            No answer recorded.
                          </p>
                        )}
                      </div>
                      {canEdit ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 flex-none px-1.5 text-[11px]"
                          onClick={() => beginEditingQuestion(aq)}
                          title="Edit this answer"
                          aria-label={`Edit answer to: ${
                            aq.questionText ?? aq.question?.QuestionText ?? 'question'
                          }`}
                        >
                          <Pencil className="mr-1 h-3 w-3" aria-hidden />
                          Edit
                        </Button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        ) : answeredQuestions.length === 0 && closedTranscript.length > 0 ? (
          // Closed / read-only cases: show the plain history transcript
          // (no editing — the case is no longer clinician-owned).
          <div className="space-y-2">
            <Label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Earlier answers
            </Label>
            <ol className="space-y-2">
              {closedTranscript.map((aq, idx) => (
                <li
                  key={`${idx}-${aq.question}`}
                  className="rounded-xl border border-border bg-muted/20 p-3"
                >
                  <div className="flex items-start gap-2">
                    <span className="mt-0.5 inline-flex h-4 w-4 flex-none items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                      <Check className="h-3 w-3" aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="text-xs font-medium leading-relaxed whitespace-pre-wrap">
                        {aq.question}
                      </p>
                      {aq.answers.length > 0 ? (
                        <ul className="space-y-0.5">
                          {aq.answers.map((ans, aIdx) => (
                            <li
                              key={aIdx}
                              className="text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap"
                            >
                              {ans}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs text-muted-foreground italic">No answer recorded.</p>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        ) : null}

        {isWaiting ? (
          <Alert className="order-first">
            <AlertDescription className="text-xs">
              Insurance is reviewing this prior authorization. No action is needed from you.
            </AlertDescription>
          </Alert>
        ) : null}

        {hasPendingQuestion && activeQuestion ? (
          <section className="order-first space-y-4 rounded-2xl border border-primary/40 bg-primary/5 p-4">
            <div>
              <h3 className="text-base font-semibold">
                {isEditingPrevious ? 'Edit answer' : 'Insurance needs your answer'}
              </h3>
              {!isEditingPrevious ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  Answer this question to keep the prior authorization moving.
                </p>
              ) : null}
            </div>
            <p className="text-sm font-medium leading-relaxed whitespace-pre-wrap">
              {activeQuestion.QuestionText ?? '—'}
            </p>

            {(answerType === 'Select' || answerType === 'Multiselect') &&
            activeQuestion.AnswerChoices ? (
              <div className="space-y-2">
                <Label className="text-xs">
                  {answerType === 'Select' ? 'Choose one' : 'Choose one or more'}
                </Label>
                <div className="flex flex-col gap-2">
                  {activeQuestion.AnswerChoices.map((choice) => {
                    const id = choice.QuestionChoiceId ?? -1;
                    const checked = selectedChoices.includes(id);
                    return (
                      <label key={id} className="flex items-start gap-2 text-xs leading-relaxed">
                        {answerType === 'Select' ? (
                          <input
                            type="radio"
                            name={`prior-auth-question-${activeQuestionId ?? 'current'}`}
                            checked={checked}
                            onChange={() => toggleChoice(id)}
                            className="mt-0.5 h-4 w-4 accent-primary"
                          />
                        ) : (
                          <Checkbox checked={checked} onCheckedChange={() => toggleChoice(id)} />
                        )}
                        <span>{choice.QuestionChoiceText ?? '—'}</span>
                      </label>
                    );
                  })}
                </div>
                {additionalTextRequirement !== 'hidden' ? (
                  <div>
                    <Label className="text-xs">
                      Additional information
                      {additionalTextRequirement === 'required' ? ' (required)' : ' (optional)'}
                    </Label>
                    <Textarea
                      value={freeTextAnswer}
                      onChange={(e) => setFreeTextAnswer(e.target.value)}
                      rows={2}
                      className="text-xs"
                      aria-required={additionalTextRequirement === 'required'}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}

            {answerType === 'FreeText' ? (
              <div>
                <Label className="text-xs">Answer</Label>
                <Textarea
                  value={freeTextAnswer}
                  onChange={(e) => setFreeTextAnswer(e.target.value)}
                  rows={3}
                  className="text-xs"
                />
              </div>
            ) : null}

            {answerType === 'Numeric' ? (
              <div>
                <Label className="text-xs">Answer (numeric)</Label>
                <Input
                  type="number"
                  value={numericAnswer}
                  onChange={(e) => setNumericAnswer(e.target.value)}
                  className="h-7 text-xs"
                />
              </div>
            ) : null}

            {answerType === 'Date' || answerType === 'DateTime' ? (
              <div>
                <Label className="text-xs">
                  {answerType === 'Date' ? 'Answer (date)' : 'Answer (date & time)'}
                </Label>
                <Input
                  type={answerType === 'Date' ? 'date' : 'datetime-local'}
                  value={dateAnswer}
                  onChange={(e) => setDateAnswer(e.target.value)}
                  className="h-7 text-xs"
                />
              </div>
            ) : null}

            <p className="text-xs leading-relaxed text-muted-foreground">
              You can change this answer until the completed form is sent to insurance.
            </p>
          </section>
        ) : !detailsQuery.isLoading &&
          !isTerminal &&
          !isWaiting &&
          presentedActions.primary === 'submit' ? (
          <section className="order-first rounded-2xl border border-primary/30 bg-primary/5 p-4">
            <h3 className="text-base font-semibold">Ready to send</h3>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
              All questions are answered. Send this prior authorization to insurance for review.
            </p>
          </section>
        ) : !detailsQuery.isLoading &&
          !isTerminal &&
          !isWaiting &&
          presentedActions.primary === 'send_prescription' ? (
          <section className="order-first rounded-2xl border border-primary/30 bg-primary/5 p-4">
            <h3 className="text-base font-semibold">Ready for the pharmacy</h3>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
              The insurance decision is complete. Send the prescription to the patient’s pharmacy.
            </p>
          </section>
        ) : null}

        {caseRequiresAttachment && !hasPendingQuestion ? (
          uploadedAttachmentCount > 0 ? (
            <section className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 inline-flex h-7 w-7 flex-none items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600">
                  <Check className="h-4 w-4" aria-hidden />
                </span>
                <div>
                  <h3 className="text-sm font-semibold">Supporting document added</h3>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    The completed prior authorization will be sent to insurance automatically.
                  </p>
                </div>
              </div>
            </section>
          ) : attachmentStep === 'answers' ? (
            <section className="space-y-3 rounded-xl border border-primary/30 bg-primary/5 p-4">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 inline-flex h-8 w-8 flex-none items-center justify-center rounded-full bg-primary/10 text-primary">
                  <ListChecks className="h-4 w-4" aria-hidden />
                </span>
                <div>
                  <h3 className="text-sm font-semibold">Questions complete</h3>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    Review the answers above. Insurance also requires a supporting document before
                    this request can be sent.
                  </p>
                </div>
              </div>
              <div className="flex justify-end">
                <Button size="sm" onClick={() => setAttachmentStep('letter')}>
                  Continue to supporting letter
                  <ArrowRight className="ml-1 h-4 w-4" aria-hidden />
                </Button>
              </div>
            </section>
          ) : (
            <section className="space-y-4 rounded-xl border border-primary/30 bg-primary/5 p-4">
              <input
                ref={fileInputRef}
                type="file"
                onChange={(event) => void handleFilePick(event)}
                className="sr-only"
                aria-label="Upload supporting document"
              />

              {lmnDraft ? (
                <>
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-8 w-8 flex-none items-center justify-center rounded-lg border bg-background text-primary">
                      <FileText className="h-4 w-4" aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold">Supporting letter ready</h3>
                      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                        {lmnDraft.summary}
                      </p>
                      {lmnDraft.flagged_gaps.length > 0 ? (
                        <p className="mt-2 text-xs font-medium text-amber-600 dark:text-amber-400">
                          {lmnDraft.flagged_gaps.length}{' '}
                          {lmnDraft.flagged_gaps.length === 1 ? 'item needs' : 'items need'} review
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => setLetterReviewOpen(true)}>
                      Review and edit letter
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={submitting || !presentedActions.upload}
                    >
                      <Paperclip className="mr-1 h-3.5 w-3.5" aria-hidden />
                      Use another document
                    </Button>
                  </div>
                </>
              ) : lmnDrafting ? (
                <div className="flex items-start gap-3 py-2" role="status" aria-live="polite">
                  <span className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  </span>
                  <div>
                    <h3 className="text-sm font-semibold">Drafting the supporting letter</h3>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                      {lmnProgress}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <h3 className="text-sm font-semibold">Add supporting documentation</h3>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                      Create an editable Letter of Medical Necessity from this chart, or upload an
                      existing document.
                    </p>
                  </div>
                  {lmnError ? (
                    <p className="text-xs text-destructive" role="alert">
                      {lmnError}
                    </p>
                  ) : null}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      onClick={() => void handleCreateSupportingLetter()}
                      disabled={submitting || !presentedActions.upload}
                    >
                      <Sparkles className="mr-1 h-3.5 w-3.5" aria-hidden />
                      Draft supporting letter
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={submitting || !presentedActions.upload}
                    >
                      <Paperclip className="mr-1 h-3.5 w-3.5" aria-hidden />
                      Upload existing document
                    </Button>
                  </div>
                </>
              )}

              <Button
                size="sm"
                variant="ghost"
                className="px-0 text-xs text-muted-foreground"
                onClick={() => setAttachmentStep('answers')}
              >
                Review insurance answers
              </Button>
            </section>
          )
        ) : null}

        {lmnDraft ? (
          <LmnDraftReview
            draft={lmnDraft}
            patientUserId={patientUserId}
            markdown={lmnMarkdown}
            onMarkdownChange={setLmnMarkdown}
            open={letterReviewOpen}
            onOpenChange={setLetterReviewOpen}
            onAttached={() => {
              setLetterReviewOpen(false);
              void detailsQuery.refetch();
            }}
          />
        ) : null}

        {prescriptionQuery.data ? (
          <details className="group rounded-xl border border-border bg-muted/20 p-3">
            <summary className="cursor-pointer list-none text-xs font-medium">
              Prescription details
            </summary>
            <div className="mt-3 border-t border-border pt-3">
              <LabeledFieldGrid
                size="sm"
                items={[
                  {
                    label: 'Quantity',
                    value:
                      prescriptionQuery.data.Quantity == null
                        ? 'Not available'
                        : `${prescriptionQuery.data.Quantity}${
                            prescriptionQuery.data.Refills == null
                              ? ''
                              : `, ${prescriptionQuery.data.Refills} refills`
                          }`,
                  },
                  { label: 'Pharmacy', value: pharmacyDisplayName ?? 'Not available' },
                  { label: 'Diagnosis', value: diagnosis ?? 'Not available' },
                  {
                    label: 'Substitutions',
                    value:
                      prescriptionQuery.data.NoSubstitutions === true ? 'Not allowed' : 'Allowed',
                  },
                ]}
              />
            </div>
          </details>
        ) : prescriptionQuery.isLoading ? (
          <div className="text-xs text-muted-foreground">Loading prescription…</div>
        ) : null}

        {isTerminal ? (
          <Alert className="order-first">
            <AlertDescription className="text-xs">
              {canAppeal
                ? `This prior authorization was ${currentStatus?.toLowerCase()}. You can file an appeal using the menu below.`
                : `This prior authorization is closed (${currentStatus}). No further answers can be submitted.`}
            </AlertDescription>
          </Alert>
        ) : null}

        {appealOpen ? (
          <div className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-2.5">
            <Label className="text-xs font-medium">Appeal reason</Label>
            <Textarea
              value={appealReason}
              onChange={(e) => setAppealReason(e.target.value)}
              rows={3}
              className="text-xs"
              placeholder="Explain why this decision should be reconsidered…"
            />
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setAppealOpen(false);
                  setAppealReason('');
                  setFormError(null);
                }}
                className="text-xs"
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleAppealConfirm}
                className="text-xs"
                disabled={submitting || appealReason.trim().length === 0}
              >
                File appeal
              </Button>
            </div>
          </div>
        ) : null}

        {(dosespotCase?.case.Attachments ?? []).length > 0 ? (
          <div className="space-y-2">
            <Label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Attachments
            </Label>
            <ul className="space-y-2">
              {(dosespotCase?.case.Attachments ?? []).map((att, idx) => {
                const fileName = att.FileName ?? `attachment-${att.AttachmentId ?? idx}`;
                const Icon = attachmentIcon(fileName);
                const uploaded = formatAttachmentDate(att.CreatedDateStamp ?? null);
                const attachmentIdRaw = att.AttachmentId;
                const attachmentId =
                  typeof attachmentIdRaw === 'number'
                    ? attachmentIdRaw
                    : typeof attachmentIdRaw === 'string'
                      ? Number(attachmentIdRaw)
                      : null;
                const canDownload =
                  recordId.length > 0 && attachmentId !== null && !Number.isNaN(attachmentId);
                return (
                  <li
                    key={`${att.AttachmentId ?? idx}-${fileName}`}
                    className="rounded-md border border-border bg-muted/30 p-2"
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 flex-none text-muted-foreground" aria-hidden />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-medium leading-tight">{fileName}</p>
                        {uploaded ? (
                          <p className="text-[11px] leading-tight text-muted-foreground">
                            Uploaded {uploaded}
                          </p>
                        ) : null}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs"
                        disabled={!canDownload}
                        onClick={() => {
                          if (!canDownload || attachmentId === null) {
                            return;
                          }
                          downloadPriorAuthAttachment(recordId, attachmentId)
                            .then((blob) => {
                              triggerBrowserDownload(
                                blob,
                                fileName || `pa-${caseId}-attachment-${attachmentId}`
                              );
                            })
                            .catch((err: unknown) => {
                              const msg = err instanceof Error ? err.message : 'Unknown error';
                              toast.error('Could not download attachment', {
                                description: msg,
                              });
                              logger.error('priorAuth.attachment.download.failed', {
                                caseId,
                                attachmentId,
                                err,
                              });
                            });
                        }}
                        title="Download"
                        aria-label={`Download ${fileName}`}
                      >
                        <Download className="h-3.5 w-3.5" aria-hidden />
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>

      <div className="flex flex-none items-center justify-between gap-2 border-t border-border pt-2">
        <div className="flex items-center gap-1">
          <PrescriptionCancelButton
            patientUserId={patientUserId}
            target={{ prescriptionRecordId: detailsQuery.data?.prescriptionRecordId ?? '' }}
            disabled={submitting || !detailsQuery.data?.prescriptionRecordId}
          />
          {hasOverflowActions ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="ghost" className="text-xs" disabled={submitting}>
                  <MoreHorizontal className="mr-1 h-4 w-4" aria-hidden />
                  More options
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" side="top">
                {showAppeal ? (
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      setAppealOpen(true);
                      setFormError(null);
                    }}
                  >
                    Appeal…
                  </DropdownMenuItem>
                ) : null}
                {showSelfPay ? (
                  <DropdownMenuItem onSelect={() => setDecision('SELF_PAY')}>
                    Send as self-pay…
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          {isEditingPrevious ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={cancelEditingQuestion}
              className="text-xs"
              disabled={submitting}
            >
              Discard changes
            </Button>
          ) : null}
          {hasPrimaryAction ? (
            primaryMode === 'answer_question' || primaryMode === 'submit' ? (
              <Button
                size="sm"
                onClick={() => void handlePrimary()}
                disabled={primaryDisabled}
                className="text-xs"
              >
                {primaryLabel}
              </Button>
            ) : (
              <HoldToSubmit
                onSubmit={handlePrimary}
                disabled={primaryDisabled}
                label={primaryLabel}
              />
            )
          ) : null}
        </div>
      </div>

      {decision ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pa-decision-title"
        >
          <div className="mx-4 w-full max-w-sm space-y-3 rounded-md border border-border bg-background p-4 shadow-lg">
            <h3 id="pa-decision-title" className="text-sm font-semibold">
              {decision === 'SELF_PAY' ? 'Send as self-pay' : 'End treatment'}
            </h3>
            <p className="text-xs text-muted-foreground">
              Add a short reason so this decision is clear in the record.
            </p>
            <Textarea
              value={decisionReason}
              onChange={(event) => setDecisionReason(event.target.value)}
              placeholder="Reason"
              rows={3}
            />
            {decision === 'SELF_PAY' ? (
              <label className="flex items-center gap-2 text-xs">
                <Checkbox
                  checked={patientAgreed}
                  onCheckedChange={(checked) => setPatientAgreed(checked === true)}
                />
                The patient agreed to self-pay.
              </label>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDecision(null);
                  setDecisionReason('');
                  setPatientAgreed(false);
                }}
              >
                Back
              </Button>
              <Button size="sm" onClick={() => void handleDecision()} disabled={submitting}>
                Confirm
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
